import jsQR from "jsqr";
import { Dropdown } from "primereact/dropdown";
import React from "react";
import { FixtureService } from "src/app/services/Index";
import { TicketScanService } from "src/app/services/TicketScanService";
import "./AdminScanning.css";

type Verdict = {
	admitted: boolean;
	code: string;
	message: string;
	ticketType?: string;
	holderName?: string;
};

type State = {
	fixtures: any[];
	fixtureId: number | null;
	scanning: boolean;
	verdict: Verdict | null;
	manual: string;
	summary: any;
	error: string | null;
	device: string;
};

/** How long a verdict stays on screen before the next person steps up. */
const VERDICT_MS = 2600;

/** Don't re-submit the same code while it's still in front of the lens. */
const SAME_CODE_COOLDOWN_MS = 3000;

/**
 * The gate.
 *
 * Built for someone standing outdoors holding a phone with a queue in front of
 * them: one enormous green or red panel, a line of words they can read out, and
 * nothing else competing for attention. Every design decision here follows from
 * that — not from what looks good in a screenshot.
 *
 * Manual entry sits underneath because cameras fail. A cracked lens, a dead
 * torch, a ticket printed on paper — the gate cannot stop working because the
 * camera won't focus.
 */
export class AdminScanning extends React.Component<{}, State> {
	private video = React.createRef<HTMLVideoElement>();
	private canvas = document.createElement("canvas");
	private stream: MediaStream | null = null;
	private frame = 0;
	private verdictTimer: any = null;
	private lastCode = "";
	private lastCodeAt = 0;

	constructor(props: {}) {
		super(props);
		this.state = {
			device: `Gate ${Math.floor(Math.random() * 90 + 10)}`,
			error: null,
			fixtureId: null,
			fixtures: [],
			manual: "",
			scanning: false,
			summary: null,
			verdict: null
		};
	}

	componentDidMount() {
		this.loadFixtures();
	}

	componentWillUnmount() {
		this.stop();
		clearTimeout(this.verdictTimer);
	}

	async loadFixtures() {
		try {
			const res = await FixtureService.getAll();
			if (!res.ok) return;
			const all = await res.json();
			const upcoming = (Array.isArray(all) ? all : [])
				.filter((f: any) => new Date(f.fixtureDateTime) > new Date(Date.now() - 6 * 60 * 60 * 1000))
				.sort((a: any, b: any) => +new Date(a.fixtureDateTime) - +new Date(b.fixtureDateTime));

			// Default to the next game. On a matchday that is nearly always the
			// right one, and one fewer thing to set up in the cold.
			this.setState({ fixtureId: upcoming[0]?.id ?? null, fixtures: upcoming }, () => this.loadSummary());
		} catch {
			this.setState({ error: "Couldn't load fixtures." });
		}
	}

	async loadSummary() {
		const { fixtureId } = this.state;
		if (!fixtureId) return;
		try {
			const res = await TicketScanService.summary(`fixtureId=${fixtureId}`);
			if (res.ok) this.setState({ summary: await res.json() });
		} catch { /* the counters just don't move */ }
	}

	async start() {
		try {
			// The back camera. facingMode is a request, not a guarantee, but on
			// a phone held up to a ticket it is the one that matters.
			this.stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: "environment" }
			});
			if (this.video.current) {
				this.video.current.srcObject = this.stream;
				await this.video.current.play();
			}
			this.setState({ error: null, scanning: true }, () => this.tick());
		} catch {
			this.setState({
				error: "No camera. Use the box below to type the code instead.",
				scanning: false
			});
		}
	}

	stop() {
		cancelAnimationFrame(this.frame);
		this.stream?.getTracks().forEach((t) => t.stop());
		this.stream = null;
		this.setState({ scanning: false });
	}

	/** Pull a frame, look for a code, submit it. */
	tick = () => {
		const video = this.video.current;
		if (!this.state.scanning || !video || video.readyState !== video.HAVE_ENOUGH_DATA) {
			this.frame = requestAnimationFrame(this.tick);
			return;
		}

		this.canvas.width = video.videoWidth;
		this.canvas.height = video.videoHeight;
		const context = this.canvas.getContext("2d", { willReadFrequently: true });
		if (!context) return;

		context.drawImage(video, 0, 0, this.canvas.width, this.canvas.height);
		const image = context.getImageData(0, 0, this.canvas.width, this.canvas.height);
		const found = jsQR(image.data, image.width, image.height, { inversionAttempts: "dontInvert" });

		if (found?.data) {
			const now = Date.now();
			const isRepeat = found.data === this.lastCode && now - this.lastCodeAt < SAME_CODE_COOLDOWN_MS;
			if (!isRepeat) {
				this.lastCode = found.data;
				this.lastCodeAt = now;
				this.submit(found.data);
			}
		}

		this.frame = requestAnimationFrame(this.tick);
	};

	async submit(token: string) {
		const { device, fixtureId } = this.state;
		if (!token.trim()) return;

		try {
			const res = await TicketScanService.redeem({ device, fixtureId, token: token.trim() });
			if (!res.ok) {
				this.showVerdict({ admitted: false, code: "error", message: "Couldn't reach the server. Try again." });
				return;
			}
			this.showVerdict(await res.json());
			this.loadSummary();
		} catch {
			// Deliberately NOT "not a valid ticket". A network failure and a
			// forged ticket need opposite reactions from whoever is on the gate.
			this.showVerdict({ admitted: false, code: "error", message: "No connection. Try again." });
		}
	}

	showVerdict(verdict: Verdict) {
		clearTimeout(this.verdictTimer);
		this.setState({ manual: "", verdict });
		if (navigator.vibrate) navigator.vibrate(verdict.admitted ? 60 : [80, 60, 80]);
		this.verdictTimer = setTimeout(() => this.setState({ verdict: null }), VERDICT_MS);
	}

	render() {
		const { error, fixtureId, fixtures, manual, scanning, summary, verdict } = this.state;

		return (
			<div className="scan">
				<div className="scan-bar">
					<Dropdown
						onChange={(e) => this.setState({ fixtureId: e.value }, () => this.loadSummary())}
						optionLabel="label"
						optionValue="value"
						options={fixtures.map((f: any) => ({
							label: `${new Date(f.fixtureDateTime).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} — ${f.teamName || f.team?.teamName || "Fixture"}`,
							value: f.id
						}))}
						placeholder="Pick the fixture"
						value={fixtureId}
					/>
					{summary && <div className="scan-counts">
						<span><strong>{summary.admitted}</strong> in</span>
						<span><strong>{summary.outstanding}</strong> to come</span>
					</div>}
				</div>

				{verdict && <div className={`scan-verdict ${verdict.admitted ? "yes" : "no"}`}>
					<span className="scan-mark">{verdict.admitted ? "✓" : "✕"}</span>
					<strong>{verdict.admitted ? "Let them in" : "Stop"}</strong>
					<p>{verdict.message}</p>
					{verdict.ticketType && <span className="scan-meta">
						{verdict.ticketType}{verdict.holderName ? ` · ${verdict.holderName}` : ""}
					</span>}
				</div>}

				<div className="scan-camera">
					<video muted playsInline ref={this.video} />
					{!scanning && <div className="scan-idle">
						<button className="scan-btn" onClick={() => this.start()} type="button">
							Start scanning
						</button>
					</div>}
					{scanning && <div className="scan-reticle" />}
				</div>

				{scanning &&
					<button className="scan-btn secondary" onClick={() => this.stop()} type="button">Stop</button>}

				{error && <p className="scan-error">{error}</p>}

				<div className="scan-manual">
					<label htmlFor="scan-manual-input">Or type the code</label>
					<div>
						<input
							autoCapitalize="off"
							autoComplete="off"
							autoCorrect="off"
							id="scan-manual-input"
							onChange={(e) => this.setState({ manual: e.target.value })}
							onKeyDown={(e) => { if (e.key === "Enter") this.submit(manual); }}
							value={manual}
						/>
						<button className="scan-btn" onClick={() => this.submit(manual)} type="button">Check</button>
					</div>
				</div>
			</div>
		);
	}
}
